<template>
  <VueFinalModal
    v-model="isOpen"
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-5xl h-[85vh] bg-[#fafbfc] rounded-[32px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
    @close="close"
  >
    <div class="flex w-full h-full min-h-0">
      <aside class="w-72 bg-[#1c4848] text-white p-8 flex flex-col justify-between relative overflow-hidden shrink-0">
        <div class="absolute inset-0 opacity-10 mix-blend-overlay pointer-events-none" style="background-image: url('https://grainy-gradients.vercel.app/noise.svg')" />
        <div class="relative z-10">
          <div class="flex items-center gap-3 mb-6 opacity-80">
            <div class="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-[#ecd79f]">
              <IconMemoryGame class="w-[18px] h-[18px]" />
            </div>
            <span class="text-xs font-bold tracking-[0.2em] uppercase">Playing</span>
          </div>
          <h3 class="font-serif text-3xl font-medium mb-1 tracking-tight text-[#f2f4f4]">
            {{ (gameLoaded || game)?.name || 'Memory' }}
          </h3>
          <p class="text-xs text-white/50 font-medium uppercase tracking-wider mb-8">Memory Match</p>
          <div class="space-y-4">
            <div class="bg-white/5 rounded-2xl p-4 border border-white/5">
              <div class="flex items-center gap-2 text-[#ecd79f] mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span class="text-[10px] font-bold uppercase tracking-wider">Time Elapsed</span>
              </div>
              <p class="text-3xl font-bold font-mono tracking-tight">{{ timerLabel }}</p>
            </div>
            <div class="bg-white/5 rounded-2xl p-4 border border-white/5">
              <div class="flex items-center gap-2 text-[#ecd79f] mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                <span class="text-[10px] font-bold uppercase tracking-wider">Moves</span>
              </div>
              <p class="text-3xl font-bold font-mono tracking-tight">{{ gameStats.moves }}</p>
            </div>
            <div v-if="hasWon" class="p-4 rounded-2xl bg-[#ecd79f]/20 border border-[#ecd79f]/40">
              <p class="font-serif text-lg font-bold text-[#ecd79f] mb-1">{{ winMessage }}</p>
              <p class="text-xs text-white/70">{{ timerLabel }} · {{ gameStats.moves }} moves</p>
            </div>
          </div>
        </div>
        <div class="relative z-10 space-y-3">
          <button type="button" class="w-full py-3.5 rounded-xl bg-[#ecd79f] text-[#1c4848] font-bold text-sm hover:bg-[#ffe082] hover:shadow-lg hover:shadow-[#ecd79f]/20 transition-all flex items-center justify-center gap-2" @click="onRestart">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            Restart Game
          </button>
          <button type="button" class="w-full py-3.5 rounded-xl bg-white/5 text-white/60 font-bold text-sm hover:bg-white/10 hover:text-white transition-colors" @click="close">Exit to Library</button>
        </div>
      </aside>
      <main class="flex-1 bg-[#f2f4f4] relative p-8 flex flex-col items-center justify-center overflow-y-auto min-h-0">
        <div v-if="!ready" class="text-[#1c4848]/50 text-sm">Loading game…</div>
        <MemoryGamePlay v-else-if="gameLoaded" ref="memoryPlayRef" :game="gameLoaded" @win="onWin" @stats="gameStats = $event" />
      </main>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import MemoryGamePlay from '@/components/app/dashboard/games/play/MemoryGamePlay.vue';
import IconMemoryGame from '@/components/app/dashboard/games/icons/IconMemoryGame.vue';
import { useGamesStore } from '@/store/games/index.js';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  game: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue', 'close']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const gamesStore = useGamesStore();
const gameLoaded = ref(null);
const gameStats = ref({ moves: 0, time: 0 });
const hasWon = ref(false);
const memoryPlayRef = ref(null);

const winMessage = computed(() => (gameLoaded.value?.config?.win_message) || 'Mashallah! Great Job!');

const timerLabel = computed(() => {
  const s = gameStats.value.time || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
});

const ready = computed(() => {
  const g = gameLoaded.value || props.game;
  if (!g?.id) return false;
  const assets = g.assets || [];
  const total = getTotalCards(g);
  return Array.isArray(assets) && assets.length >= total / 2;
});

function getTotalCards(g) {
  const cfg = g?.config || {};
  const s = (cfg.grid_size || '3x4').toString();
  const [c, r] = s.split('x').map(Number);
  const cols = c && !Number.isNaN(c) ? c : 3;
  const rows = r && !Number.isNaN(r) ? r : 4;
  return cols * rows;
}

watch(() => [props.modelValue, props.game?.id], async () => {
  if (!props.modelValue || !props.game?.id) {
    gameLoaded.value = null;
    gameStats.value = { moves: 0, time: 0 };
    hasWon.value = false;
    return;
  }
  const hasAssets = (props.game.assets || []).length >= getTotalCards(props.game) / 2;
  if (hasAssets) {
    gameLoaded.value = props.game;
    return;
  }
  try {
    const loaded = await gamesStore.fetchOne(props.game.id);
    gameLoaded.value = loaded;
  } catch {
    gameLoaded.value = props.game;
  }
}, { immediate: true });

function close() {
  emit('update:modelValue', false);
  emit('close');
}

function onRestart() {
  hasWon.value = false;
  memoryPlayRef.value?.restart?.();
}

function onWin() {
  hasWon.value = true;
}
</script>
